import { useState, useCallback, useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { BannerMessageV2 } from 'ui-components/components/banner-message/banner-message-v2';
import { BannerStatus } from 'ui-components/components/banner-message/banner-enums';
import { DropdownMenu } from 'ui-components/components/dropdown-menu';
import { DropdownMenuItem } from 'ui-components/components/dropdown-menu-item/index';
import { DropdownMenuToggler } from 'ui-components/components/dropdown-menu-toggler';
import { FlexColumn } from 'ui-components/components/flex/column';
import { Gap } from 'ui-components/components/flex/gap';
import { FlexRow } from 'ui-components/components/flex/row';
import { InlineTabs } from 'ui-components/components/tabs/inline-tabs';
import { Input } from 'ui-components/components/input';
import { DropdownMenuPosition } from 'ui-components/components/INTERNAL/dropdown/dropdown-menu-position';
import { LoadingIndicator } from 'ui-components/components/loading-indicator';
import { Modal } from 'ui-components/components/modal';
import { ModalBody } from 'ui-components/components/modal/modal-body';
import { ModalFooter } from 'ui-components/components/modal/modal-footer';
import { ModalHeader } from 'ui-components/components/modal/modal-header';
import { Text, TextType } from 'ui-components/components/text';
import { THEME_CONSTANTS } from 'ui-components/services/button-theme-service';

import { EditorSection } from './components/editor-instance';
import { EditorModalFooter } from './components/modal-footer';
import { TemplatePreview } from './components/template-preview';
import styles from './styles.module.less';
import { FIELD_REPLACEMENT_SETS } from '../../../../../../components/editor/constants';
import { useLLMActionBinding } from '../../../../../../components/shippy-assistant/hooks/use-llm-action-binding';
import { useLLMViewContext } from '../../../../../../components/shippy-assistant/hooks/use-llm-view-context';
import { PACKING_SLIP_UPDATE_ACTION_ID } from '../../../../../../components/shippy-assistant/llm-action-registry/action-definitions';
import { EXECUTOR_SLOT } from '../../../../../../components/shippy-assistant/llm-action-registry/types';
import { useEnableNewLayoutOptions } from '../../../../../../feature-flags/hooks/use-enable-new-layout-options';
import { HEADER, ITEMS_HEADER, ITEMS, FOOTER, defaultTemplateSizes, newTemplateSizes } from '../../../constants';
import messages from '../../../messages';

/**
 * @typedef {import('../../../types').TemplateSize} TemplateSize
 */

/**
 *
 * @param {object} props
 * @param {string} [props.footer] - code for the footer parsed as a string
 * @param {string} [props.header] - code for the header parsed as a string
 * @param {boolean} props.isCopy - flag to determine if the template is a copy or not
 * @param {boolean} props.isSaving - flag to determine if the template is saving to show a loading indicator
 * @param {string} [props.items] - code for the order items parsed as a string
 * @param {string} [props.itemsHeader] - code for the order items table headers parsed as a string
 * @param {string} [props.name] - name of the template
 * @param {() => void} props.onCancel - callback function to cancel/close modal
 * @param {TemplateSize} props.size - size/format of the template
 * @param {(templateName: string, header: string, itemsHeader: string, orderItems: string, footer: string, size: string) => void} props.onSave - callback function to save the template
 * @returns {JSX.Element}
 */
export const TemplateEditorModal = ({
  footer = '',
  header = '',
  isCopy,
  isSaving,
  items = '',
  itemsHeader = '',
  name = '',
  onCancel,
  size,
  onSave,
}) => {
  const intl = useIntl();
  const calculatedName = isCopy ? `${intl.formatMessage(messages.copySuffix, { name })}` : name;
  // TODO: [SPD-19601]: Remove this logic when feature flag is removed
  const enableNewLayoutOptions = useEnableNewLayoutOptions();
  const sizeOptions = useMemo(
    () => (enableNewLayoutOptions ? [...defaultTemplateSizes, ...newTemplateSizes] : defaultTemplateSizes),
    [enableNewLayoutOptions],
  );

  const findTemplateOption = (selectedSizeTemplate) => {
    return sizeOptions.find((option) => option.value === selectedSizeTemplate);
  };
  const [templateNameValue, setTemplateNameValue] = useState(calculatedName);
  const [templateSizeValue, setTemplateSizeValue] = useState(() => findTemplateOption(size));
  const [hasNameError, setHasNameError] = useState(false);
  const onTemplateNameChange = useCallback(
    (e) => {
      if (hasNameError) {
        setHasNameError(false);
      }
      setTemplateNameValue(e.target.value);
    },
    [hasNameError],
  );

  const [openSections, setOpenSections] = useState(() => new Set([HEADER, ITEMS_HEADER, ITEMS, FOOTER]));
  const onSectionClick = useCallback((e) => {
    const clickedSection = e.currentTarget.getAttribute('data-name');
    setOpenSections((prev) => {
      const next = new Set(prev);
      if (next.has(clickedSection)) {
        next.delete(clickedSection);
      } else {
        next.add(clickedSection);
      }
      return next;
    });
  }, []);

  const [headerValue, setHeaderValue] = useState(header);
  const [itemsHeaderValue, setItemsHeaderValue] = useState(itemsHeader);
  const [orderItemsValue, setOrderItemsValue] = useState(items);
  const [footerValue, setFooterValue] = useState(footer);
  const [isAiGenerated, setIsAiGenerated] = useState(false);

  useLLMActionBinding(
    PACKING_SLIP_UPDATE_ACTION_ID,
    (params) => {
      if (params.header) setHeaderValue(params.header);
      if (params.itemsHeader !== undefined) setItemsHeaderValue(params.itemsHeader);
      if (params.items !== undefined) setOrderItemsValue(params.items);
      if (params.footer !== undefined) setFooterValue(params.footer);
      if (params.name) setTemplateNameValue(params.name);
      if (params.size) setTemplateSizeValue(findTemplateOption(params.size));
      setIsAiGenerated(true);
      return { success: true };
    },
    { slot: EXECUTOR_SLOT },
  );

  useLLMViewContext(PACKING_SLIP_UPDATE_ACTION_ID, () => ({
    name: templateNameValue,
    size: templateSizeValue?.value,
    header: headerValue,
    itemsHeader: itemsHeaderValue,
    items: orderItemsValue,
    footer: footerValue,
  }));

  // Tab system for Preview/Code tabs
  const defaultTabIndex = isAiGenerated ? 0 : 1;

  const tabsData = useMemo(
    () => [
      {
        tabId: 'preview',
        title: intl.formatMessage(messages.previewTab),
        content: (
          <TemplatePreview
            footer={footerValue}
            header={headerValue}
            items={orderItemsValue}
            itemsHeader={itemsHeaderValue}
            size={templateSizeValue?.value}
          />
        ),
      },
      {
        tabId: 'code',
        title: intl.formatMessage(messages.codeTab),
        content: (
          <div className={styles.editors}>
            <EditorSection
              fieldReplacementSet={FIELD_REPLACEMENT_SETS.packingSlip}
              isOpen={openSections.has(HEADER)}
              onChange={setHeaderValue}
              onSectionClick={onSectionClick}
              sectionKey={HEADER}
              titleMessage={messages.editorHeaderTitle}
              value={headerValue}
            />
            <EditorSection
              fieldReplacementSet={FIELD_REPLACEMENT_SETS.packingSlip}
              isOpen={openSections.has(ITEMS_HEADER)}
              onChange={setItemsHeaderValue}
              onSectionClick={onSectionClick}
              sectionKey={ITEMS_HEADER}
              titleMessage={messages.editorItemsHeaderTitle}
              value={itemsHeaderValue}
            />
            <EditorSection
              fieldReplacementSet={FIELD_REPLACEMENT_SETS.packingSlipOrderItems}
              isOpen={openSections.has(ITEMS)}
              onChange={setOrderItemsValue}
              onSectionClick={onSectionClick}
              sectionKey={ITEMS}
              titleMessage={messages.editorItemsTitle}
              value={orderItemsValue}
            />
            <EditorSection
              fieldReplacementSet={FIELD_REPLACEMENT_SETS.packingSlip}
              isOpen={openSections.has(FOOTER)}
              onChange={setFooterValue}
              onSectionClick={onSectionClick}
              sectionKey={FOOTER}
              titleMessage={messages.editorFooterTitle}
              value={footerValue}
            />
          </div>
        ),
      },
    ],
    [intl, footerValue, headerValue, orderItemsValue, itemsHeaderValue, templateSizeValue, openSections, onSectionClick],
  );

  const onSaveClicked = () => {
    if (!templateNameValue || !templateNameValue.length) {
      setHasNameError(true);
      return;
    }
    onSave(templateNameValue, headerValue, itemsHeaderValue, orderItemsValue, footerValue, templateSizeValue?.value);
  };

  const onSizeOptionClick = (selectedSizeTemplate) => {
    const selectedSize = findTemplateOption(selectedSizeTemplate);
    setTemplateSizeValue(selectedSize);
  };

  const headerTitle = name && name.length ? messages.modalHeaderUpdate : messages.modalHeaderCreate;
  return (
    <Modal className={styles['template-editor-modal']} size='large'>
      <ModalHeader>
        <FormattedMessage {...headerTitle} />
      </ModalHeader>
      <ModalBody className={styles['modal-body']}>
        {isSaving ? (
          <LoadingIndicator className={styles.loading} />
        ) : (
          <>
            {isAiGenerated ? (
              <BannerMessageV2
                className={styles['ai-banner']}
                content={<FormattedMessage {...messages.aiGeneratedBanner} />}
                status={BannerStatus.Info}
              />
            ) : null}
            <FlexRow className={styles['template-info']} gap={Gap.Space24}>
              <FlexColumn className={styles.field}>
                <Text textType={TextType.Label} value={messages.name} />
                <Input
                  exposeNativeEventInCallbacks
                  hasError={hasNameError}
                  name='templateName'
                  onChange={onTemplateNameChange}
                  value={templateNameValue}
                />
              </FlexColumn>
              <FlexColumn className={styles.field}>
                <Text textType={TextType.Label} value={messages.selectSizeLabel} />
                <DropdownMenu
                  dropdownMenuShouldHaveWidthOfButton
                  fullWidth
                  menuPosition={DropdownMenuPosition.BottomRight}
                  selected={templateSizeValue}
                >
                  <DropdownMenuToggler theme={THEME_CONSTANTS.white}>
                    {templateSizeValue ? (
                      <FormattedMessage {...templateSizeValue.label} />
                    ) : (
                      <FormattedMessage {...messages.selectSizeLabel} />
                    )}
                  </DropdownMenuToggler>
                  {sizeOptions.map((option) => (
                    <DropdownMenuItem key={option.value} onClick={() => onSizeOptionClick(option.value)}>
                      <div>
                        <FormattedMessage {...option.label} />
                      </div>
                    </DropdownMenuItem>
                  ))}
                </DropdownMenu>
              </FlexColumn>
            </FlexRow>
            <InlineTabs data={tabsData} defaultTabIndex={defaultTabIndex} key={isAiGenerated ? 'ai' : 'manual'} />
          </>
        )}
      </ModalBody>
      <ModalFooter className={styles.footer}>
        <EditorModalFooter
          onCancel={onCancel}
          onSaveClicked={onSaveClicked}
          template={{
            name: templateNameValue,
            header: headerValue,
            itemsHeader: itemsHeaderValue,
            items: orderItemsValue,
            footer: footerValue,
            size,
          }}
        />
      </ModalFooter>
    </Modal>
  );
};
